<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <section class="list-page">
            <header class="list-page__head">
              <div class="list-page__heading">
                <h2 class="list-page__title">Subscriptions</h2>
                <p class="list-page__count">{{ countLabel }}</p>
              </div>
              <button type="button" class="list-page__action" :disabled="isLoading" v-on:click="loadSubscriptions">
                <i :class="['pi', isLoading ? 'pi-spin pi-spinner' : 'pi-refresh']"></i>
                <span>Refresh</span>
              </button>
            </header>

            <div class="subscriptions-toolbar">
              <div class="search-box">
                <i class="pi pi-search"></i>
                <input
                  v-model.trim="searchTerm"
                  type="search"
                  class="form-control shadow-none"
                  placeholder="Search by account, plan or organization…"
                />
              </div>
            </div>

            <div class="list-page__table">
              <tableLoader v-if="isLoading" message="Loading subscriptions…" />
              <div v-if="!isLoading" class="table-scroller">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Account</th>
                      <th scope="col">Plan</th>
                      <th scope="col">Organization</th>
                      <th scope="col">Status</th>
                      <th scope="col">Started</th>
                      <th scope="col">Renews / Expires</th>
                      <th scope="col" class="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(sub, index) in filteredSubscriptions"
                      v-bind:key="sub.id || index"
                      class="sub-row"
                      v-on:click="viewSubscription(sub)"
                    >
                      <th scope="row">{{ rowNumber(index) }}</th>
                      <td class="row-name">{{ accountName(sub) }}</td>
                      <td><span class="plan-pill">{{ planName(sub) }}</span></td>
                      <td>{{ organizationName(sub) || '—' }}</td>
                      <td><span class="badge" :class="statusInfo(sub).badgeCls">{{ statusInfo(sub).label }}</span></td>
                      <td>{{ $filters.getDateOnly(sub.startDate) }}</td>
                      <td>{{ $filters.getDateOnly(sub.endDate) }}</td>
                      <td class="col-actions">
                        <button type="button" class="view-btn" v-on:click.stop="viewSubscription(sub)">View</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="!isLoading && filteredSubscriptions.length === 0" class="list-page__empty">
                <i class="pi pi-credit-card"></i>
                <div>{{ searchTerm ? 'No subscriptions match your search.' : 'No subscriptions found.' }}</div>
              </div>
            </div>

            <pagination
              v-if="!isLoading && metaData"
              :meta-data="metaData"
              :current-page="currentPage"
              :per-page="perPage"
              v-on:change="onPageChange"
            />
          </section>
        </div>
      </div>
    </div>

    <subscriptionDetailModal
      v-if="showDetailModal && selectedSubscription"
      :subscription="selectedSubscription"
      @close="showDetailModal = false"
    />
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import tableLoader from "@/components/Table_Loader.vue";
import pagination from "@/components/Pagination.vue";
import subscriptionDetailModal from "@/components/subscription/Subscription_Detail_Modal.vue";
import Subscription from "@/services/subscription";

export default {
  title: "Subscriptions",
  components: {
    sidenav,
    navbar,
    tableLoader,
    pagination,
    subscriptionDetailModal,
  },
  data() {
    return {
      subscriptions: [],
      metaData: null,
      isLoading: false,
      currentPage: 1,
      perPage: 30,
      searchTerm: "",
      selectedSubscription: null,
      showDetailModal: false,
    };
  },
  computed: {
    filteredSubscriptions() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.subscriptions;
      return this.subscriptions.filter((sub) => {
        const haystack = [
          this.accountName(sub),
          this.planName(sub),
          this.organizationName(sub),
          this.statusInfo(sub).label,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },
    countLabel() {
      const total = this.metaData ? this.metaData.totalRecords : this.subscriptions.length;
      return total === 1 ? "1 subscription" : `${total} subscriptions`;
    },
  },
  methods: {
    rowNumber(index) {
      return (this.currentPage - 1) * this.perPage + index + 1;
    },
    planName(sub) {
      return sub.subscriptionPlan ? sub.subscriptionPlan.name : "—";
    },
    organizationName(sub) {
      return sub.organization ? sub.organization.name : "";
    },
    // The org's owner is the account the subscription is for; show their name.
    accountName(sub) {
      const owner = sub.organization && sub.organization.user ? sub.organization.user : null;
      if (owner) return this.$filters.fullName(owner.fname, owner.lname) || "—";
      if (sub.createdByUser) {
        return this.$filters.fullName(sub.createdByUser.fname, sub.createdByUser.lname) || "—";
      }
      return this.organizationName(sub) || "—";
    },
    statusInfo(sub) {
      if (sub.isPending) return { label: "Pending", badgeCls: "badge-warning" };
      if (!sub.isActive) return { label: "Inactive", badgeCls: "badge-secondary" };
      const ended = sub.endDate && new Date(sub.endDate).getTime() < Date.now();
      if (ended) return { label: "Expired", badgeCls: "badge-danger" };
      return { label: "Active", badgeCls: "badge-success" };
    },
    async loadSubscriptions() {
      this.isLoading = true;
      try {
        const res = await Subscription.getSubscriptions({ page: this.currentPage, limit: this.perPage });
        if (res?.data?.status) {
          this.subscriptions = res.data.data || [];
          this.metaData = res.data.metaData || null;
        }
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange({ page, perPage }) {
      this.currentPage = page;
      this.perPage = perPage;
      this.loadSubscriptions();
    },
    viewSubscription(sub) {
      this.selectedSubscription = sub;
      this.showDetailModal = true;
    },
  },
  mounted() {
    this.loadSubscriptions();
  },
};
</script>
<style scoped>
.subscriptions-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1 1 340px;
  max-width: 460px;
}

.search-box .pi {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: var(--ehs-color-soft);
  font-size: 0.85rem;
}

.search-box .form-control {
  padding-left: 36px !important;
}

.sub-row {
  cursor: pointer;
  transition: background 0.12s ease;
}

.sub-row:hover {
  background: var(--ehs-color-primary-soft);
}

.plan-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-canvas);
  border: 1px solid var(--ehs-color-border);
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--ehs-color-text);
  text-transform: capitalize;
}

.view-btn {
  border: 0;
  background: transparent;
  padding: 4px 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-primary-active);
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s ease;
}

.view-btn:hover {
  color: var(--ehs-color-primary-hover);
  text-decoration: underline;
}

.view-btn:focus-visible {
  outline: none;
  text-decoration: underline;
}
</style>
